import { FileUploadStatusEnum } from '../enums/FileUploadStatusEnum';
import type UploadFile from '../interfaces/UploadFile';
import { db } from './index';
import {
  UPLOAD_BLOB_MAX_TOTAL_BYTES,
  UPLOAD_QUEUE_PERSISTENCE_ENABLED,
  UPLOAD_SESSION_TTL_MS,
} from './schema';

export type PersistedUploadQueueItem = Omit<UploadFile, 'file' | 'children'>;

export interface UploadQueueSession {
  sessionId: string;
  userId: number;
  isQuickShare: boolean;
  quickShareForm: {
    users: unknown[];
    groups: unknown[];
    permission: string;
    expiration: string;
    hasMessage: boolean;
    message: string;
  };
  targetFolderUid: number | null;
}

export interface LoadedUploadQueue {
  session: UploadQueueSession;
  items: UploadFile[];
}

function stripFileForPersistence(item: UploadFile): PersistedUploadQueueItem {
  const { file: _file, children: _children, ...metadata } = item;
  return metadata;
}

function totalBlobBytes(items: UploadFile[]): number {
  return items.reduce((total, item) => {
    if (item.isFolder || !item.file) return total;
    return total + item.file.size;
  }, 0);
}

/** ponytail: one active session per userId — new batch overwrites prior IDB session. */
export async function evictExpiredSessions(): Promise<void> {
  if (!UPLOAD_QUEUE_PERSISTENCE_ENABLED) return;
  try {
    const now = Date.now();
    const expired = await db.uploadSessions.where('expiresAt').below(now).toArray();
    for (const session of expired) {
      await clearUploadQueue(session.sessionId);
    }
  } catch {
    // No-op on IDB errors
  }
}

export async function clearUploadQueue(userIdOrSessionId: number | string): Promise<void> {
  if (!UPLOAD_QUEUE_PERSISTENCE_ENABLED) return;
  try {
    let sessionIds: string[];
    if (typeof userIdOrSessionId === 'number') {
      const sessions = await db.uploadSessions.where('userId').equals(userIdOrSessionId).toArray();
      sessionIds = sessions.map((session) => session.sessionId);
    } else {
      sessionIds = [userIdOrSessionId];
    }

    for (const sessionId of sessionIds) {
      await db.uploadQueueItems.where('sessionId').equals(sessionId).delete();
      await db.uploadQueueBlobs.where('sessionId').equals(sessionId).delete();
      await db.uploadSessions.delete(sessionId);
    }
  } catch {
    // No-op
  }
}

export async function persistUploadQueue(
  session: UploadQueueSession,
  items: UploadFile[],
): Promise<void> {
  if (!UPLOAD_QUEUE_PERSISTENCE_ENABLED) return;
  try {
    await evictExpiredSessions();

    const blobBytes = totalBlobBytes(items);
    if (blobBytes > UPLOAD_BLOB_MAX_TOTAL_BYTES) {
      console.warn('[uploadQueue] Blob total exceeds cap; skipping IDB persist');
      return;
    }

    const now = Date.now();
    const sessionEntry = {
      sessionId: session.sessionId,
      userId: session.userId,
      isQuickShare: session.isQuickShare,
      quickShareForm: JSON.stringify(session.quickShareForm),
      targetFolderUid: session.targetFolderUid,
      createdAt: now,
      updatedAt: now,
      expiresAt: now + UPLOAD_SESSION_TTL_MS,
    };

    await db.transaction(
      'rw',
      db.uploadSessions,
      db.uploadQueueItems,
      db.uploadQueueBlobs,
      async () => {
        const priorSessions = await db.uploadSessions
          .where('userId')
          .equals(session.userId)
          .toArray();
        for (const prior of priorSessions) {
          if (prior.sessionId !== session.sessionId) {
            await db.uploadQueueItems.where('sessionId').equals(prior.sessionId).delete();
            await db.uploadQueueBlobs.where('sessionId').equals(prior.sessionId).delete();
            await db.uploadSessions.delete(prior.sessionId);
          }
        }

        await db.uploadSessions.put(sessionEntry);
        await db.uploadQueueItems.where('sessionId').equals(session.sessionId).delete();
        await db.uploadQueueBlobs.where('sessionId').equals(session.sessionId).delete();

        for (const item of items) {
          const normalizedStatus =
            item.status === FileUploadStatusEnum.Uploading
              ? FileUploadStatusEnum.Pending
              : item.status;
          const metadata = stripFileForPersistence({ ...item, status: normalizedStatus });
          await db.uploadQueueItems.put({
            itemId: item.id,
            sessionId: session.sessionId,
            metadata: JSON.stringify(metadata),
          });
          if (!item.isFolder && item.file) {
            await db.uploadQueueBlobs.put({
              itemId: item.id,
              sessionId: session.sessionId,
              blob: item.file,
            });
          }
        }
      },
    );
  } catch {
    // No-op on quota or DB errors; in-memory path still works
  }
}

export async function loadUploadQueue(userId: number): Promise<LoadedUploadQueue | null> {
  if (!UPLOAD_QUEUE_PERSISTENCE_ENABLED) return null;
  try {
    await evictExpiredSessions();
    const now = Date.now();
    const sessions = await db.uploadSessions.where('userId').equals(userId).toArray();
    const session = sessions
      .filter((entry) => entry.expiresAt >= now)
      .sort((left, right) => right.updatedAt - left.updatedAt)[0];
    if (!session) return null;

    const itemEntries = await db.uploadQueueItems
      .where('sessionId')
      .equals(session.sessionId)
      .toArray();
    if (itemEntries.length === 0) return null;

    const blobEntries = await db.uploadQueueBlobs
      .where('sessionId')
      .equals(session.sessionId)
      .toArray();
    const blobsByItemId = new Map(blobEntries.map((entry) => [entry.itemId, entry.blob]));

    const items: UploadFile[] = itemEntries.map((entry) => {
      const metadata = JSON.parse(entry.metadata) as PersistedUploadQueueItem;
      const blob = blobsByItemId.get(entry.itemId);
      const file =
        !metadata.isFolder && blob && metadata.fileName
          ? new File([blob], metadata.fileName, { type: blob.type || 'application/octet-stream' })
          : undefined;
      return {
        ...metadata,
        file,
        children: [],
        status:
          metadata.status === FileUploadStatusEnum.Uploading
            ? FileUploadStatusEnum.Pending
            : metadata.status,
      };
    });

    return {
      session: {
        sessionId: session.sessionId,
        userId: session.userId,
        isQuickShare: session.isQuickShare,
        quickShareForm: JSON.parse(session.quickShareForm),
        targetFolderUid: session.targetFolderUid,
      },
      items,
    };
  } catch {
    return null;
  }
}

export function isQueuePersistable(
  items: UploadFile[],
  options: {
    userId: number | undefined;
    pendingFilePasswordRequest: unknown;
    quickShareFormEmpty: boolean;
    isQuickShareSession: boolean;
  },
): boolean {
  if (!UPLOAD_QUEUE_PERSISTENCE_ENABLED || options.userId == null) return false;
  if (options.pendingFilePasswordRequest != null) return false;
  if (options.isQuickShareSession && options.quickShareFormEmpty) return false;

  const hasRecoverable = items.some(
    (item) =>
      item.status === FileUploadStatusEnum.Pending ||
      item.status === FileUploadStatusEnum.Uploading,
  );
  if (!hasRecoverable) return false;

  const hasTerminal = items.some(
    (item) =>
      item.status === FileUploadStatusEnum.Canceled ||
      (item.status === FileUploadStatusEnum.Failed &&
        (item.retries >= 3 || item.skipRetryDisplay === true)),
  );
  if (hasTerminal) return false;

  return totalBlobBytes(items) <= UPLOAD_BLOB_MAX_TOTAL_BYTES;
}
